/* ==========================================================================
   creator-gifs.js
   Opens creator tiles in a lightbox instead of navigating away.

   A tile opens in the lightbox when the section has "Open videos in a lightbox"
   ticked and the tile has something to play: a Vimeo / YouTube link (played in
   an iframe) or a direct video file URL (played in a <video>). Any other link —
   TikTok, Instagram, a normal page — falls through to a normal click, so
   nothing breaks if the setting is off.

   Load from sections/creator-gifs.liquid:
     <script src="{{ 'creator-gifs.js' | asset_url }}" defer="defer"></script>
   ========================================================================== */

const CreatorGifs = {
  /* Turn a watch URL into an autoplaying embed URL. Returns null if we don't
     recognise the host, which means "let the browser follow the link". */
  embedUrl(href) {
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      // /123456789            -> public
      // /123456789/abcdef123  -> unlisted, second part is the privacy hash
      const parts = url.pathname.split('/').filter(Boolean);
      const id = parts.find((p) => /^\d+$/.test(p));
      if (!id) return null;
      const hash = parts[parts.indexOf(id) + 1] || url.searchParams.get('h');
      let src = 'https://player.vimeo.com/video/' + id + '?autoplay=1&title=0&byline=0&portrait=0';
      if (hash) src += '&h=' + encodeURIComponent(hash);
      return src;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
      return id ? 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0' : null;
    }
    if (host === 'youtu.be') {
      const id = url.pathname.slice(1);
      return id ? 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0' : null;
    }

    return null;
  },

  /* What a tile should play, if anything.
       {kind:'embed', src} -> Vimeo / YouTube page link, played in an iframe
       {kind:'file',  src} -> a direct video file, played in a <video>
     A tile's Link wins over its Video file URL, so a tile can show one clip and
     open a different one. */
  source(tile) {
    const link = tile.querySelector('.creator-gifs__link');
    if (link) {
      const embed = this.embedUrl(link.getAttribute('href'));
      if (embed) return { kind: 'embed', src: embed };
      return null; // an unrecognised link: let the browser follow it
    }
    const file = tile.getAttribute('data-video');
    if (file) return { kind: 'file', src: file };
    return null;
  },

  /* Every lightbox-able tile in the same section, in DOM order. */
  siblings(tile) {
    const section = tile.closest('.creator-gifs');
    return Array.from(section.querySelectorAll('.creator-gifs__tile')).filter((t) =>
      this.source(t)
    );
  },

  open(list, index) {
    this.list = list;
    this.index = index;
    if (!this.el) this.build();
    this.render();
    document.body.style.overflow = 'hidden';
    this.el.classList.add('is-open');
    this.el.querySelector('.creator-gifs-lightbox__close').focus();
  },

  close() {
    if (!this.el) return;
    this.el.classList.remove('is-open');
    this.el.querySelector('.creator-gifs-lightbox__frame').src = 'about:blank';
    const video = this.el.querySelector('.creator-gifs-lightbox__video');
    video.pause();
    video.removeAttribute('src');
    document.body.style.overflow = '';
    if (this.opener) this.opener.focus();
  },

  step(d) {
    if (!this.list || this.list.length < 2) return;
    this.index = (this.index + d + this.list.length) % this.list.length;
    this.render();
  },

  render() {
    const tile = this.list[this.index];
    const source = this.source(tile);
    const frame = this.el.querySelector('.creator-gifs-lightbox__frame');
    const video = this.el.querySelector('.creator-gifs-lightbox__video');

    if (source.kind === 'embed') {
      video.pause();
      video.removeAttribute('src');
      video.hidden = true;
      frame.hidden = false;
      frame.src = source.src;
    } else {
      frame.src = 'about:blank';
      frame.hidden = true;
      video.hidden = false;
      video.src = source.src;
      video.currentTime = 0;
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
    }

    this.el.querySelector('.creator-gifs-lightbox__label').textContent =
      tile.getAttribute('data-label') || '';
    const nav = this.el.querySelector('.creator-gifs-lightbox__nav');
    nav.hidden = this.list.length < 2;
    this.el.querySelector('.creator-gifs-lightbox__counter').textContent =
      this.index + 1 + ' / ' + this.list.length;
  },

  build() {
    const el = document.createElement('div');
    el.className = 'creator-gifs-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML = [
      '<div class="creator-gifs-lightbox__scrim" data-close></div>',
      '<div class="creator-gifs-lightbox__panel">',
      '  <button type="button" class="creator-gifs-lightbox__close" aria-label="Close">&times;</button>',
      '  <div class="creator-gifs-lightbox__stage">',
      '    <iframe class="creator-gifs-lightbox__frame" src="about:blank" title="Video"',
      '      frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>',
      '    <video class="creator-gifs-lightbox__video" controls playsinline hidden></video>',
      '  </div>',
      '  <div class="creator-gifs-lightbox__nav">',
      '    <button type="button" class="creator-gifs-lightbox__arrow" data-step="-1">&larr; Prev</button>',
      '    <span class="creator-gifs-lightbox__counter"></span>',
      '    <button type="button" class="creator-gifs-lightbox__arrow" data-step="1">Next &rarr;</button>',
      '  </div>',
      '  <p class="creator-gifs-lightbox__label"></p>',
      '</div>',
    ].join('');
    document.body.appendChild(el);
    this.el = el;

    el.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-close') || e.target.closest('.creator-gifs-lightbox__close')) {
        this.close();
        return;
      }
      const arrow = e.target.closest('[data-step]');
      if (arrow) this.step(Number(arrow.getAttribute('data-step')));
    });
  },

  init() {
    if (this._bound) return;
    this._bound = true;

    document.addEventListener('click', (e) => {
      const tile = e.target.closest('.creator-gifs__tile');
      if (!tile) return;
      const section = tile.closest('.creator-gifs');
      if (!section || section.getAttribute('data-lightbox') === 'false') return;

      const source = this.source(tile);
      if (!source) {
        console.warn(
          '[creator-gifs] tile has nothing to play. Set the tile Link to a Vimeo/YouTube URL, or Video file URL to a direct .mp4.',
          tile
        );
        return;
      }

      e.preventDefault();
      this.opener = tile;
      const list = this.siblings(tile);
      this.open(list, list.indexOf(tile));
    });

    document.addEventListener('keydown', (e) => {
      if (!this.el || !this.el.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowRight') this.step(1);
      if (e.key === 'ArrowLeft') this.step(-1);
    });
  },
};

CreatorGifs.init();
document.addEventListener('shopify:section:load', () => CreatorGifs.init());
window.CreatorGifs = CreatorGifs;
